'use client';

import { AnalyticsClient } from '@/analytics/AnalyticsClient';
import { isFullStoryAllowed } from '@/common/initialization/fullstory';
import { getPIIFilteredSession } from '@/common/utils/getPiiFilteredSession';
import { notifyError } from '@/common/utils/notifyError';
import type { ClientAuthenticationSession } from '@/store/session';
import { useSignInStore } from '@/store/signInStore';
import { FullStory } from '@fullstory/browser';

enum Partition {
  verizon = 'verizon',
  att = 'att',
  asurion = 'asurion',
}

const partitions = Object.values(Partition);

const getPartition = (partner: string) =>
  partitions.includes(partner as Partition) ? partner : Partition.asurion;

const getProperties = (
  session: ClientAuthenticationSession,
  extraData?: Record<string, unknown>,
) => {
  const piiFilteredSession = getPIIFilteredSession(
    useSignInStore.getState().session,
  );

  return [
    {
      env: process.env.ENVIRONMENT_NAME,
      session: { ...session, ...piiFilteredSession },
      id: session.id,
      OriginalAsurionIdSessionId: session.originalSessionId,
      OidcClientId: session.clientId,
      locale: session.locale,
      CampaignId: session.campaignId,
      interactionId: session.interactionId,
      Origin: session.origin,
      Partner: session.partner,
      AsurionIdSessionId: session.asurionSessionId,
      UserBrowserId: session.userBrowserId,
      ...extraData,
    },
    {
      anonymousId: session.asurionSessionId,
    },
    {
      Client: getPartition(session.partner),
    },
  ] as const;
};

const internalPublishEvent = (
  session: ClientAuthenticationSession,
  eventName: string,
  extraData: Record<string, unknown>,
) => {
  const [properties, additionalProperties, metadataProperties] = getProperties(
    session,
    extraData,
  );

  /* istanbul ignore next */
  try {
    if (isFullStoryAllowed) {
      FullStory('trackEvent', {
        name: 'AsurionIDSession_'.concat(eventName),
        properties,
      });
    }
  } catch (e) {
    notifyError(e as Error);
  }

  try {
    if (properties.AsurionIdSessionId) {
      AnalyticsClient.updateSession(properties.AsurionIdSessionId);
    }

    AnalyticsClient.dispatcher.dispatch(eventName, {
      ExtraData: { ...properties, ...additionalProperties },
      MetaData: { ...metadataProperties, sendToOpenSearch: true },
    });
  } catch (e) {
    notifyError(e as Error);
  }
};

export const publishEvent = (
  session: ClientAuthenticationSession,
  eventName: string,
  elementId: string,
  extraData?: Record<string, unknown>,
) => internalPublishEvent(session, eventName, { ...extraData, elementId });

export const publishPageEvent = (
  session: ClientAuthenticationSession,
  eventName: string,
  location: string,
  extraData?: Record<string, unknown>,
) => internalPublishEvent(session, eventName, { location, ...extraData });
